'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import {
  ControlledAccordionBody,
  ControlledAccordionHeader,
  ControlledAccordionRoot,
} from '@umodeapporg/ui/components/UI/ControlledAccordion';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SURFACE_CARD } from '@/components/surface/surfaces';
import { cn } from '@/lib/utils';

import { buildTelas, IAjudaDuvida } from '../helpers/ajudaConteudo';
import { destinoDaDuvida } from '../helpers/ajudaDestino';

interface IDuvidaItemProps {
  duvida: IAjudaDuvida;
  aberta: boolean;
  onToggle: () => void;
}

/**
 * UMA dúvida, sempre na mesma ordem: a frase que responde, o passo a passo quando a
 * pergunta é "como faço", o botão que leva ao lugar, quem faz, e a regra inteira
 * recolhida.
 *
 * Mora fora da lista porque são quatro lugares que mostram a MESMA pergunta: a tarefa, a
 * tela, a busca e o painel do "?" de dentro da tela. Uma cópia por lugar seria a mesma
 * resposta escrita quatro vezes, e a divergência entre elas só apareceria para quem usa.
 *
 * O texto completo de antes não foi cortado: ele é o "Entender a regra". O que mudou é
 * que a pessoa lê sessenta palavras para resolver, em vez de duzentas para achar as
 * sessenta.
 */
export function DuvidaItem({ duvida, aberta, onToggle }: IDuvidaItemProps) {
  const { t } = useTranslation('common');
  const telas = useMemo(() => buildTelas(t), [t]);
  const [regraAberta, setRegraAberta] = useState(false);
  const destino = destinoDaDuvida(duvida, telas, t);

  return (
    <article className={cn(SURFACE_CARD, 'px-4 py-3')}>
      <ControlledAccordionRoot open={aberta}>
        <ControlledAccordionHeader
          onClick={onToggle}
          className="gap-3 border-b-0 py-0 text-sm font-semibold text-foreground"
        >
          <span className="min-w-0 flex-1">{duvida.pergunta}</span>
        </ControlledAccordionHeader>
        <ControlledAccordionBody className="pb-0 pt-3 text-sm font-normal text-foreground-muted">
          <div className="flex flex-col gap-3">
            {duvida.resumo !== '' && <p className="font-semibold text-foreground">{duvida.resumo}</p>}

            {duvida.passos.length > 0 && (
              <ol className="flex list-decimal flex-col gap-1 pl-5">
                {duvida.passos.map((passo) => (
                  <li key={passo}>{passo}</li>
                ))}
              </ol>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {destino !== null && (
                <LinkButton href={destino.href} variant="outlined" tone="neutral" size="sm" className="normal-case">
                  <MaterialSymbol icon="arrow_forward" size={16} className="mr-1.5 shrink-0" aria-hidden />
                  {t('ajuda.duvidas.abrir', { alvo: destino.rotulo })}
                </LinkButton>
              )}
              {duvida.quem !== '' && (
                <span className="text-xs text-foreground-subtle">{`${t('ajuda.duvidas.quem')}: ${duvida.quem}`}</span>
              )}
            </div>

            {/* A regra inteira continua aqui, palavra por palavra: recolhida, e não cortada. */}
            <div className="flex flex-col gap-2 border-t border-border pt-2">
              <ControlledButton
                defaultHtmlButton
                type="button"
                onClick={() => setRegraAberta((atual) => !atual)}
                aria-expanded={regraAberta}
                className="interactive flex w-fit items-center gap-x-1 text-xs font-semibold normal-case text-foreground-muted"
              >
                <MaterialSymbol icon={regraAberta ? 'expand_less' : 'expand_more'} size={16} aria-hidden />
                {t('ajuda.duvidas.entenderRegra')}
              </ControlledButton>
              {regraAberta && <p className="animate-fade-in">{duvida.resposta}</p>}
            </div>
          </div>
        </ControlledAccordionBody>
      </ControlledAccordionRoot>
    </article>
  );
}

interface IDuvidasListaProps {
  duvidas: Array<IAjudaDuvida>;
  /** A que abre já expandida, quando o endereço pediu uma (a busca pede). */
  inicial?: string;
}

/**
 * Uma lista de dúvidas em que UMA resposta fica aberta por vez: a lista é o índice, e dez
 * respostas abertas enterram a que se procurava.
 */
export function DuvidasLista({ duvidas, inicial }: IDuvidasListaProps) {
  const { t } = useTranslation('common');
  const [aberta, setAberta] = useState<string | null>(inicial ?? null);

  if (duvidas.length === 0) {
    return <NoDataFound variant="empty" compact title={t('ajuda.duvidas.semDuvidaAqui')} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {duvidas.map((duvida) => (
        <DuvidaItem
          key={duvida.id}
          duvida={duvida}
          aberta={aberta === duvida.id}
          onToggle={() => setAberta(aberta === duvida.id ? null : duvida.id)}
        />
      ))}
    </div>
  );
}
